import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import { DataScienceEnums } from 'dashboard-services/api/dataScience';
import TimeService from 'dashboard-services/services/TimeService';

import { builderPipelineCreatorActions } from 'actions/modules/datascience';

import { DataPrepPicker, FilePicker, PipelinePicker, SymbolPicker } from 'primary/pickers';

import Boolean from './Boolean';
import Cron from './Cron';
import DataSharingProvider from './DataSharingProvider';
import Date from './Date';
import Email from './Email';
import Group from './Group';
import HolidayCalendar from './HolidayCalendar';
import JustError from './JustError';
import MetadataFilter from './MetadataFilter';
import MetadataKey from './MetadataKey';
import MultiSelect from './MultiSelect';
import Password from './Password';
import Select from './Select';
import Slack from './Slack';
import SymbolColumn from './SymbolColumn';
import SymbolFilter from './SymbolFilter';
import SymbolKey from './SymbolKey';
import SymbolValue from './SymbolValue';
import Text from './Text';

import { When } from 'react-if';

import './Body.scss';

const EMPTY_ARRAY = [];
const EMPTY_FUN = () => {}
const Body = ({
  className,
  value = undefined,
  valueType,
  name,
  optional,
  taskName,
  options = EMPTY_ARRAY,
  onChange = EMPTY_FUN,
  disabled,
  size = undefined,
  withBoolean = false,
  preserveArrayValues = false,
  fileExtension = undefined,
  fileType = undefined,
  withShowPassword = false,
  ref,
  ...props
}) => {
  const dispatch = useDispatch(),
        fileExtensions = useMemo(() => fileExtension ? [fileExtension] : undefined, [fileExtension]),
        onChangeValue = useCallback((value, name) => !disabled && onChange(value, name), [disabled, onChange]),
        onChangeDate = useCallback(value => !disabled && onChange(value, name), [disabled, onChange, name]),
        onChangeText = useCallback((value, { name }) => !disabled && onChange(value, name), [disabled, onChange]),
        onChangeMultiSelect = useCallback(value => !disabled && onChange(preserveArrayValues ? value || EMPTY_ARRAY : value?.toString() || "", name), [disabled, name, onChange, preserveArrayValues]),
        onChangeSelect = useCallback(value => !disabled && onChange(value || "", name), [disabled, onChange, name]),
        onChangeBoolean = useCallback(() => {
          if(!disabled) {
            const val = value === undefined || value === "" || String(value).toLowerCase() === "false" ? false : true
            onChange(!val, name)
          }
        }, [disabled, value, onChange, name]),
        onChangeBooleanPause = useCallback(() => {
          if(!disabled) {
            const val = value === undefined || value === "" || String(value).toLowerCase() === "false" ? false : true
            onChange(!val, name)
            dispatch(builderPipelineCreatorActions.changeSelectedTaskProp(!val, "isPaused"))
          }
        }, [disabled, dispatch, name, onChange, value])
  const refEl = useRef()

  const validators = valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.NUMBER 
          ? [{
              message: "Type only numbers.",
              validator: val => val.isNumber({ max: Number.MAX_SAFE_INTEGER, min: Number.MIN_SAFE_INTEGER }) 
            }]
          : EMPTY_ARRAY

  const commonProps = useMemo(() => ({
    ...props,
    disabled,
    name,
    ref: refEl,
    required: !optional
  }), [optional, name, refEl, disabled, props])

  useEffect(() => {
    refEl.current && refEl.current.validateForForm()
  }, [refEl])

  return (
    <div className={className}>
      <When condition={valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.STRING || valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.SECRET || valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.NUMBER || !valueType || valueType === "" || !Object.values(DataScienceEnums.TASK_PARAM_VALUE_TYPE).includes(valueType)}>
        {() => (
          <Text
              {...commonProps}
              onChange={onChangeText}
              ref={ref}
              size={size}
              validators={validators}
              value={value}
              valueType={valueType}
          />
        )}
      </When>
      <When condition={valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.PASSWORD}>
        {() => (
          <Password
              {...commonProps}
              onChange={onChangeText}
              ref={ref}
              size={size}
              validators={validators}
              value={value}
              valueType={valueType}
              withShowPassword={withShowPassword}
          />
        )}
      </When>
      <When condition={valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.SELECT}>
        {() => (
          <Select
              {...commonProps}
              onChange={onChangeSelect}
              options={options}
              ref={ref}
              size={size}
              value={value}
          />
        )}
      </When>
      <When condition={valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.HOLIDAY_CALENDAR}>
        {() => (
          <HolidayCalendar
              {...commonProps}
              onChange={onChangeSelect}
              ref={ref}
              size={size}
              value={value}
          />
        )}
      </When>
      <When condition={valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.BOOLEAN && withBoolean}>
        {() => (
          <Boolean
              {...commonProps}
              onChange={onChangeBoolean}
              ref={ref}
              value={value}
          />
        )}
      </When>
      <When condition={valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.BOOLEAN_PAUSE && withBoolean}>
        {() => (
          <Boolean
              {...commonProps}
              onChange={onChangeBooleanPause}
              ref={ref}
              value={value}
          />
        )}
      </When>
      <When condition={valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.MULTI_SELECT}>
        {() => (
          <MultiSelect
              {...commonProps}
              onChange={onChangeMultiSelect}
              options={options}
              ref={ref}
              size={size}
              value={value}
          />
        )}
      </When>
      <When condition={valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.GROUP || valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.GROUP_READ}>
        {() => (
          <Group
              {...commonProps}
              onChange={onChangeSelect}
              ref={ref}
              size={size}
              value={value}
              writeOnly={valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.GROUP}
          />
        )}
      </When>
      <When condition={valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.TEXT_AREA || valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.JSON}>
        {() => (
          <JustError
              {...commonProps}
              defaultValue={value}
              ref={ref}
          />
        )}
      </When>
      <When condition={valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.DATALAKE_FILE}>
        {() => (
          <FilePicker
              {...commonProps}
              defaultValue={value}
              fileExtensions={fileExtensions}
              fileType={fileType}
              onChange={onChangeValue}
              optionalText={`${taskName}: ${name}`}
              ref={ref}
          />
        )}
      </When>
      <When condition={valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.DATA_PREP_PARAM}>
        {() => (
          <DataPrepPicker
              {...commonProps}
              defaultValue={value}
              onChange={onChangeValue}
              optionalText={`${taskName}: ${name}`}
              ref={ref}
          />
        )}
      </When>
      <When condition={valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.PIPELINE}>
        {() => (
          <PipelinePicker
              {...commonProps}
              defaultValue={value}
              onChange={onChangeValue}
              optionalText={`${taskName}: ${name}`}
              ref={ref}
          />
        )}
      </When>
      <When condition={valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.SYMBOL}>
        {() => (
          <SymbolPicker
              {...commonProps}
              defaultValue={value}
              onChange={onChangeValue}
              optionalText={`${taskName}: ${name}`}
              ref={ref}
          />
        )}
      </When>
      <When condition={valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.EMAIL}>
        {() => (
          <Email
              {...commonProps}
              name={name}
              onChange={onChangeMultiSelect}
              ref={ref}
              size={size}
              value={value}
          />
        )}
      </When>
      <When condition={valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.SLACK_NAME}>
        {() => (
          <Slack
              {...commonProps}
              name={name}
              onChange={onChangeMultiSelect}
              ref={ref}
              size={size}
              value={value}
          />
        )}
      </When>
      <When condition={valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.DATE}>
        {() => (
          <Date
              {...commonProps}
              onChange={onChangeDate}
              ref={ref}
              validators={validators}
              value={value}
              valueType={valueType}
          />
        )}
      </When>
      <When condition={valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.TIMEZONE}>
        {() => (
          <Select
              {...commonProps}
              onChange={onChangeSelect}
              options={TimeService.TIME_ZONES}
              ref={ref}
              size={size}
              value={value}
          />
        )}
      </When>
      <When condition={valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.CRON}>
        {() => (
          <Cron
              {...commonProps}
              defaultValue={value}
              onChange={onChangeValue}
              ref={ref}
              size={size}
          />
        )}
      </When>
      <When condition={valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.METADATA_FILTER || valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.MULTI_METADATA_FILTER}>
        {() => (
          <MetadataFilter
              {...commonProps}
              isMulti={valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.MULTI_METADATA_FILTER}
              onChange={onChangeValue}
              ref={ref}
              validators={validators}
              value={value}
              valueType={valueType}
          />
        )}
      </When>
      <When condition={valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.SYMBOL_FILTER || valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.MULTI_SYMBOL_FILTER}>
        {() => (
          <SymbolFilter
              {...commonProps}
              isMulti={valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.MULTI_SYMBOL_FILTER}
              onChange={onChangeValue}
              ref={ref}
              validators={validators}
              value={value}
              valueType={valueType}
          />
        )}
      </When>
      <When condition={valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.SYMBOL_COLUMN || valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.MULTI_SYMBOL_COLUMN}>
        {() => (
          <SymbolColumn
              {...commonProps}
              isMulti={valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.MULTI_SYMBOL_COLUMN}
              onChange={onChangeMultiSelect}
              ref={ref}
              size={size}
              validators={validators}
              value={value}
              valueType={valueType}
          />
        )}
      </When>
      <When condition={valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.SYMBOL_KEY || valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.MULTI_SYMBOL_KEY}>
        {() => (
          <SymbolKey
              {...commonProps}
              isMulti={valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.MULTI_SYMBOL_KEY}
              onChange={onChangeMultiSelect}
              ref={ref}
              size={size}
              validators={validators}
              value={value}
              valueType={valueType}
          />
        )}
      </When>
      <When condition={valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.SYMBOL_VALUE || valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.MULTI_SYMBOL_VALUE}>
        {() => (
          <SymbolValue
              {...commonProps}
              isMulti={valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.MULTI_SYMBOL_VALUE}
              onChange={onChangeMultiSelect}
              ref={ref}
              size={size}
              validators={validators}
              value={value}
              valueType={valueType}
          />
        )}
      </When>
      <When condition={valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.METADATA_KEY || valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.MULTI_METADATA_KEY}>
        {() => (
          <MetadataKey
              {...commonProps}
              isMulti={valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.MULTI_METADATA_KEY}
              onChange={onChangeMultiSelect}
              ref={ref}
              size={size}
              validators={validators}
              value={value}
              valueType={valueType}
          />
        )}
      </When>
      <When condition={valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.DATA_SHARING_PROVIDER}>
        {() => (
          <DataSharingProvider
              {...commonProps}
              onChange={onChangeSelect}
              ref={ref}
              size={size}
              validators={validators}
              value={value}
          />
        )}
      </When>
    </div>
  )
}

Body.displayName = "ValueTypeResolveBody"

Body.propTypes = {
  className: PropTypes.string.isRequired, 
  disabled: PropTypes.bool.isRequired, 
  fileExtension: PropTypes.string, 
  fileType: PropTypes.string, 
  name: PropTypes.string.isRequired, 
  onChange: PropTypes.func,
  optional: PropTypes.bool.isRequired, 
  options: PropTypes.any, 
  preserveArrayValues: PropTypes.bool,
  size: PropTypes.string,
  taskName: PropTypes.string.isRequired,
  value: PropTypes.any,
  valueType: PropTypes.string.isRequired, 
  withBoolean: PropTypes.bool, 
  withShowPassword: PropTypes.bool,
}

export default memo(Body);
